/**
 * Defensive normalisers for creative-module tool outputs (creatives.list / get / batch_status /
 * plan_matrix / models). Backend shapes are not frozen yet, so every reader tolerates the common
 * envelopes (`{items}`, `{creatives}`, `{result}`…) and missing fields. Shared by Creations, Studio and
 * the creative detail modal.
 */
import { generationsOf, type Generation } from "./GenerationProgress";
import { num, pickArray, pickObject, type AdRow, type Approval, type Creative, type CreativeAsset, type Feedback, type ReferenceItem } from "@/api/types";

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);
const strArr = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);

// ─── Creatives ───────────────────────────────────────────────────────────────

export function normalizeAsset(v: unknown, parent?: { creativeId?: string; kind?: string }): CreativeAsset | null {
  if (!isObj(v)) return null;
  const aspect = str(v.aspect) ?? "1:1";
  // batch_status / list summaries omit asset ids — synthesise a stable one per creative + aspect.
  const id = str(v.id) ?? str(v.assetId) ?? (parent?.creativeId ? `${parent.creativeId}:${aspect}` : null);
  if (!id) return null;
  const url = str(v.url) ?? str(v.publicUrl) ?? str(v.providerUrl);
  const mediaType = v.mediaType === "video" || v.media_type === "video" || (!v.mediaType && parent?.kind === "video") || (url && /\.(mp4|mov|webm)(\?|$)/i.test(url)) ? "video" : "image";
  return {
    ...(v as unknown as CreativeAsset),
    id,
    aspect,
    mediaType,
    url,
    thumbnailUrl: str(v.thumbnailUrl) ?? str(v.posterUrl),
    qaReport: isObj(v.qaReport) ? (v.qaReport as unknown as CreativeAsset["qaReport"]) : null,
  };
}

export function normalizeCreative(v: unknown, extraAssets?: unknown): Creative | null {
  if (!isObj(v)) return null;
  const id = str(v.id) ?? str(v.creativeId);
  if (!id) return null;
  const declaredKind = v.kind === "video" || v.kind === "image" ? v.kind : undefined;
  const assets = [...pickArray(v.assets), ...pickArray(extraAssets)].map((a) => normalizeAsset(a, { creativeId: id, kind: declaredKind })).filter((a): a is CreativeAsset => !!a);
  const seen = new Set<string>();
  const uniq = assets.filter((a) => (seen.has(a.id) ? false : (seen.add(a.id), true)));
  const kind = declaredKind ?? (uniq.some((a) => a.mediaType === "video") ? "video" : "image");
  const tags = strArr(v.tags);
  const source = str(v.source) ?? (v.imported === 1 || v.imported === true ? "imported" : tags.includes("uploaded") ? "uploaded" : undefined);
  return {
    ...(v as unknown as Creative),
    id,
    name: str(v.name) ?? str(v.hook) ?? "Untitled creative",
    kind,
    status: str(v.status) ?? "draft",
    assets: uniq,
    overlayText: strArr(v.overlayText),
    tags,
    ...(source ? { source } : {}),
  };
}

export interface CreativeListPage {
  generations: Generation[];
  items: Creative[];
  nextCursor: string | null;
  total: number | null;
  counts: Record<string, number>;
}

export function normalizeCreativeList(v: unknown): CreativeListPage {
  const items = pickArray(v, "creatives")
    .map((c) => normalizeCreative(c))
    .filter((c): c is Creative => !!c);
  const o = isObj(v) ? v : {};
  const counts: Record<string, number> = {};
  const rawCounts = isObj(o.counts) ? o.counts : isObj(o.statusCounts) ? o.statusCounts : null;
  if (rawCounts) for (const [k, n] of Object.entries(rawCounts)) if (num(n) !== null) counts[k] = num(n) as number;
  return {
    items,
    generations: generationsOf(v),
    nextCursor: str(o.nextCursor) ?? str(o.cursor) ?? str(o.next) ?? null,
    total: num(o.total) ?? num(o.count) ?? null,
    counts,
  };
}

/** Preview media for a creative card: video creatives play their 9:16 (or first) video on hover. */
export function creativePreview(c: Creative): { url: string | null; poster: string | null; mediaType: "image" | "video" } {
  const assets = (c.assets ?? []).filter((a) => a.status !== "superseded");
  if (c.kind === "video") {
    const v = assets.find((a) => a.mediaType === "video" && a.aspect === "9:16" && a.url) ?? assets.find((a) => a.mediaType === "video" && a.url);
    if (v) return { url: v.url ?? null, poster: v.thumbnailUrl ?? c.thumbnailUrl ?? assets.find(a => a.mediaType === "image")?.url ?? null, mediaType: "video" };
    return {url:null, poster:c.thumbnailUrl ?? assets.find(a => a.mediaType === "image")?.url ?? null, mediaType:"video"};
  }
  const sq = assets.find((a) => a.aspect === "1:1" && a.mediaType === "image" && a.url);
  const img = sq ?? assets.find((a) => a.mediaType === "image" && a.url);
  return { url: c.thumbnailUrl ?? img?.url ?? null, poster: null, mediaType: "image" };
}

export function creativeAspects(c: Creative): string[] {
  const set = new Set((c.assets ?? []).filter((a) => a.status !== "superseded").map((a) => a.aspect));
  return [...set].sort((a, b) => (a === "1:1" ? -1 : b === "1:1" ? 1 : a.localeCompare(b)));
}

/** QA roll-up for a creative's live assets. */
export function creativeQa(c: Creative): "pass" | "fail" | "pending" | null {
  const assets = (c.assets ?? []).filter((a) => a.status !== "superseded");
  if (!assets.length) return null;
  const st = assets.map((a) => a.qaStatus ?? a.qaReport?.verdict ?? "pending");
  if (st.some((s) => s === "fail")) return "fail";
  if (st.every((s) => s === "pass" || s === "skipped")) return "pass";
  return "pending";
}

// ─── creatives.get ──────────────────────────────────────────────────────────

export interface VerdictRow {
  verdict: string;
  createdAt?: string | null;
  reasons: string[];
}

export interface CreativeDetail {
  creative: Creative;
  approvals: Approval[];
  feedback: Feedback[];
  /** Nearest first (parent, grandparent, …). */
  ancestors: Creative[];
  parent: Creative | null;
  children: Creative[];
  ads: AdRow[];
  verdicts: VerdictRow[];
  reference: ReferenceItem | null;
  batch: { id: string; title?: string | null; kind?: string | null; status?: string | null } | null;
}

/**
 * creatives.get → {creative, assets (every version incl. superseded), approvals, feedback,
 * lineage:{ancestors, children}, batch, card}. Older/alternate shapes (`lineage.parent`, `ads`,
 * `verdicts`, `reference`) are still accepted.
 */
export function normalizeCreativeDetail(v: unknown): CreativeDetail | null {
  if (!isObj(v)) return null;
  const rawCreative = isObj(v.creative) ? v.creative : v;
  const creative = normalizeCreative(rawCreative, v.assets);
  if (!creative) return null;
  const lineage = isObj(v.lineage) ? v.lineage : {};
  const verdicts = pickArray(v, "verdicts").flatMap((x): VerdictRow[] => {
    if (!isObj(x)) return [];
    const verdict = str(x.verdict);
    if (!verdict) return [];
    const reasons = pickArray(x.reasons)
      .map((r) => (typeof r === "string" ? r : isObj(r) ? (str(r.detail) ?? str(r.code) ?? "") : ""))
      .filter(Boolean);
    return [{ verdict, createdAt: str(x.createdAt) ?? str(x.decidedAt), reasons }];
  });
  const latest = str((rawCreative as Obj).latestVerdict);
  if (!verdicts.length && latest) verdicts.push({ verdict: latest, reasons: [] });
  const ancestors = pickArray(lineage.ancestors)
    .map((c) => normalizeCreative(c))
    .filter((c): c is Creative => !!c);
  const parent = ancestors[0] ?? normalizeCreative(lineage.parent ?? v.parent);
  const refObj = isObj(v.reference) ? v.reference : isObj(v.sourceReference) ? v.sourceReference : null;
  const batch =
    isObj(v.batch) && str(v.batch.id)
      ? { id: String(v.batch.id), title: str(v.batch.title), kind: str(v.batch.kind), status: str(v.batch.status) }
      : creative.batchId
        ? { id: creative.batchId }
        : null;
  return {
    creative,
    approvals: pickArray(v, "approvals").filter(isObj) as unknown as Approval[],
    feedback: pickArray(v, "feedback").filter(isObj) as unknown as Feedback[],
    ancestors: ancestors.length ? ancestors : parent ? [parent] : [],
    parent,
    children: pickArray(lineage.children ?? v.children)
      .map((c) => normalizeCreative(c))
      .filter((c): c is Creative => !!c),
    ads: (pickArray(v, "ads").filter(isObj) as unknown as AdRow[]).map((a) => ({ ...a, name: a.name ?? a.externalId ?? "Ad" })),
    verdicts,
    reference: refObj && str(refObj.id) ? (refObj as unknown as ReferenceItem) : null,
    batch,
  };
}

// ─── creatives.batch_status ───────────────────────────────────────────────────

export type BatchPhase = "planning" | "generating" | "qa" | "ready" | "failed" | "cancelled";

export interface BatchStatus {
  id: string;
  status: BatchPhase;
  title: string | null;
  error: string | null;
  createdAt: string | null;
  completedAt: string | null;
  creatives: Creative[];
  total: number;
  done: number;
  failed: number;
  progress: number;
  estimatedCostUsd: number | null;
  actualCostUsd: number | null;
}

const TERMINAL_ASSET = new Set(["ready", "qa_failed", "failed", "superseded"]);

export function isBatchTerminal(s: BatchPhase | string | null | undefined): boolean {
  return s === "ready" || s === "failed" || s === "cancelled";
}

export function normalizeBatchStatus(v: unknown, batchId: string): BatchStatus {
  const o = isObj(v) ? v : {};
  const b = isObj(o.batch) ? o.batch : o;
  const creatives = pickArray(o, "creatives")
    .map((c) => normalizeCreative(c))
    .filter((c): c is Creative => !!c);
  const rawStatus = str(b.status) ?? str(o.status) ?? "generating";
  const status: BatchPhase = (["planning", "generating", "qa", "ready", "failed", "cancelled"] as const).includes(rawStatus as BatchPhase) ? (rawStatus as BatchPhase) : "generating";
  const counts = isObj(o.counts) ? o.counts : isObj(o.progress) ? o.progress : {};
  const byStatus = isObj(counts.creatives) ? counts.creatives : {};
  const nestedTotal = Object.values(byStatus).reduce<number>((n, x) => n + (num(x) ?? 0), 0);
  const total = num(counts.total) ?? num(o.total) ?? (nestedTotal || creatives.length);
  const doneFromCreatives = creatives.filter((c) => {
    if (["pending_approval", "approved", "rejected", "qa_failed", "published", "live"].includes(String(c.status))) return true;
    const as = c.assets ?? [];
    return as.length > 0 && as.every((a) => TERMINAL_ASSET.has(String(a.status ?? "")));
  }).length;
  const done = num(counts.done) ?? num(counts.ready) ?? num(counts.completed) ?? doneFromCreatives;
  const failed = num(counts.failed) ?? num(byStatus.qa_failed) ?? creatives.filter((c) => c.status === "qa_failed" || c.status === "failed").length;
  const explicit = num(o.progress) ?? num(counts.pct);
  const progress = isBatchTerminal(status) ? 1 : explicit !== null ? (explicit > 1 ? explicit / 100 : explicit) : total > 0 ? done / total : 0;
  return {
    id: str(b.id) ?? batchId,
    status,
    title: str(b.title),
    error: str(b.error) ?? str(o.error),
    createdAt: str(b.createdAt),
    completedAt: str(b.completedAt),
    creatives,
    total,
    done,
    failed,
    progress: Math.max(0, Math.min(1, progress)),
    estimatedCostUsd: num(o.estimatedCostUsd) ?? num(b.estimatedCostUsd),
    actualCostUsd: num(o.costUsd) ?? num(o.actualCostUsd) ?? num(b.costUsd),
  };
}

/** batchId from any generate/iterate response. */
export function pickBatchId(v: unknown): string | null {
  if (!isObj(v)) return null;
  return str(v.batchId) ?? (isObj(v.batch) ? str(v.batch.id) : null) ?? str(v.id);
}

// ─── creatives.models ─────────────────────────────────────────────────────────

export interface ModelOption {
  id: string;
  label: string;
  kind: "image" | "video" | "edit";
  description: string;
  badge: string | null;
  tags: string[];
  aspects: string[];
  resolutions: string[];
  defaultResolution: string | null;
  durations: number[];
  defaultDuration: number | null;
  audio: boolean;
  estCostUsd: number | null;
  costUnit: "image" | "second" | null;
  isDefault: boolean;
  supportsGenerate: boolean;
  /** Images the model takes besides the prompt (edit models: including the image being edited). */
  maxReferences: number | null;
}

const TAG_LABEL: Record<string, string> = {
  "text-rendering": "Exact text",
  references: "References",
  default: "Default",
  photoreal: "Photoreal",
  typography: "Typography",
  audio: "Audio",
  "start-frame": "Start Frame",
  "end-frame": "End Frame",
  "image-to-video": "Start Frame",
  ugc: "UGC",
  fast: "Fast",
};

export function modelTagLabel(t: string): string {
  return TAG_LABEL[t] ?? t.replace(/[-_]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function normalizeModel(v: unknown, fallbackKind?: "image" | "video" | "edit"): ModelOption | null {
  if (!isObj(v)) return null;
  const id = str(v.id) ?? str(v.model) ?? str(v.slug);
  if (!id) return null;
  const k = str(v.kind) ?? str(v.type) ?? fallbackKind ?? "image";
  const kind = k === "video" ? "video" : k === "edit" ? "edit" : "image";
  const aspects = Array.isArray(v.aspects) ? strArr(v.aspects) : isObj(v.aspects) ? Object.keys(v.aspects) : [];
  const durations = (Array.isArray(v.durations) ? v.durations : []).map(num).filter((n): n is number => n !== null);
  const tags = strArr(v.tags).filter((t) => t !== "default");
  const resolutions = strArr(v.resolutions);
  const audio = v.nativeAudio === true || !!str(v.audioParam) || tags.includes("audio");
  const derived: string[] = [];
  if (audio && !tags.includes("audio")) derived.push("audio");
  if (resolutions.some((r) => /1080|2k|4k/i.test(r))) derived.push(resolutions.find((r) => /4k/i.test(r)) ?? resolutions.find((r) => /1080|2k/i.test(r)) ?? "");
  if (str(v.imageEndpoint) && kind === "video" && !tags.includes("start-frame")) derived.push("start-frame");
  const cost = isObj(v.cost) ? v.cost : {};
  const unit = str(v.costUnit) ?? str(cost.unit);
  return {
    id,
    label: str(v.label) ?? str(v.name) ?? id,
    kind,
    description: str(v.description) ?? "",
    badge: str(v.badge) ?? str(v.tier) ?? null,
    tags: [...tags, ...derived.filter(Boolean)],
    aspects,
    resolutions,
    defaultResolution: str(v.defaultResolution),
    durations,
    defaultDuration: num(v.defaultDuration),
    audio,
    estCostUsd: num(v.estCostUsd) ?? num(v.costUsd) ?? num(cost.usd) ?? num(cost.estimateUsd),
    costUnit: unit === "second" || unit === "image" ? unit : null,
    isDefault: v.isDefault === true || v.default === true || strArr(v.tags).includes("default"),
    supportsGenerate: v.supportsGenerate !== false,
    maxReferences: num(v.referenceImages) ?? num(v.maxReferenceImages),
  };
}

export interface ModelCatalog {
  image: ModelOption[];
  video: ModelOption[];
  /** Instruction-edit models (creative editor "Edit image"). */
  edit: ModelOption[];
  defaults: { imageModel: string | null; videoModel: string | null; editModel: string | null };
}

export function normalizeModels(v: unknown): ModelCatalog {
  const o = isObj(v) ? v : {};
  const out: ModelOption[] = [];
  const holder = isObj(o.models) ? o.models : o;
  if (Array.isArray(v) || Array.isArray(o.models) || Array.isArray(o.items)) {
    for (const m of pickArray(v, "models")) {
      const n = normalizeModel(m);
      if (n) out.push(n);
    }
  } else {
    for (const k of ["image", "video", "edit"] as const) {
      for (const m of pickArray(holder[k] ?? holder[`${k}Models`])) {
        const n = normalizeModel(m, k);
        if (n) out.push(n);
      }
    }
  }
  const d = isObj(o.defaults) ? o.defaults : {};
  const usable = out.filter((m) => m.supportsGenerate);
  return {
    image: usable.filter((m) => m.kind === "image"),
    video: usable.filter((m) => m.kind === "video"),
    edit: usable.filter((m) => m.kind === "edit"),
    defaults: { imageModel: str(d.imageModel) ?? str(d.image), videoModel: str(d.videoModel) ?? str(d.video), editModel: str(d.editModel) ?? str(d.edit) },
  };
}

// ─── creatives.plan_matrix ────────────────────────────────────────────────────

export interface PlanCell {
  key: string;
  /** UI-only: hook slot (row) within its angle column. */
  row: number;
  hook: string;
  hookBankId: string | null;
  hookSource: string | null;
  angle: string;
  emotionalTrigger: string;
  awarenessStage: string;
  format: string;
  layout: string | null;
  headline: string;
  primaryText: string;
  overlayText: string[];
  cta: string | null;
  visualBrief: string;
  assetIds: string[] | null;
  rationale: string | null;
}

export interface MatrixPlan {
  planId: string | null;
  concept: string;
  conceptDescription: string;
  angles: { name: string; description: string }[];
  cells: PlanCell[];
  estimatedCostUsd: number | null;
  warnings: string[];
}

let cellSeq = 0;
export function newCellKey(): string {
  cellSeq += 1;
  return `cell-${Date.now().toString(36)}-${cellSeq}`;
}

export function normalizeCell(v: unknown): PlanCell | null {
  if (!isObj(v)) return null;
  const hook = str(v.hook);
  if (!hook) return null;
  return {
    key: str(v.key) ?? str(v.id) ?? newCellKey(),
    row: num(v.row) ?? -1,
    hook,
    hookBankId: str(v.hookBankId),
    hookSource: str(v.hookSource),
    angle: str(v.angle) ?? "Core angle",
    emotionalTrigger: str(v.emotionalTrigger) ?? "curiosity",
    awarenessStage: str(v.awarenessStage) ?? "problem_aware",
    format: str(v.format) ?? "static",
    layout: str(v.layout),
    headline: str(v.headline) ?? "",
    primaryText: str(v.primaryText) ?? "",
    overlayText: strArr(v.overlayText),
    cta: str(v.cta),
    visualBrief: str(v.visualBrief) ?? "",
    assetIds: Array.isArray(v.assetIds) ? strArr(v.assetIds) : Array.isArray(v.brandAssetIds) ? strArr(v.brandAssetIds) : null,
    rationale: str(v.rationale),
  };
}

export function normalizePlan(v: unknown): MatrixPlan | null {
  if (!isObj(v)) return null;
  const p = isObj(v.plan) ? v.plan : isObj(v.matrix) ? v.matrix : v;
  const cells = pickArray(p, "cells")
    .map(normalizeCell)
    .filter((c): c is PlanCell => !!c);
  if (!cells.length) return null;
  const angles = pickArray(p, "angles").flatMap((a) => {
    if (typeof a === "string") return [{ name: a, description: "" }];
    if (isObj(a) && str(a.name)) return [{ name: String(a.name), description: str(a.description) ?? "" }];
    return [];
  });
  for (const c of cells) if (!angles.some((a) => a.name === c.angle)) angles.push({ name: c.angle, description: "" });
  // Assign hook slots (rows) per angle in planner order.
  const perAngle = new Map<string, number>();
  for (const c of cells) {
    if (c.row >= 0) continue;
    const r = perAngle.get(c.angle) ?? 0;
    c.row = r;
    perAngle.set(c.angle, r + 1);
  }
  return {
    planId: str(v.planId) ?? str(p.id) ?? null,
    concept: str(p.concept) ?? "",
    conceptDescription: str(p.conceptDescription) ?? "",
    angles,
    cells,
    estimatedCostUsd: num(v.estimatedCostUsd) ?? num(p.estimatedCostUsd),
    warnings: strArr(v.warnings).concat(strArr(p.warnings)),
  };
}

/**
 * Plan → `plan` for creatives.generate_matrix (HookMatrixSchema: {concept, conceptDescription, angles,
 * cells:[{hook, hookBankId, angle, emotionalTrigger, awarenessStage, format, layout, headline,
 * primaryText, overlayText, cta, visualBrief, assetIds, rationale}]}). Drops UI-only keys.
 */
export function planToWire(plan: MatrixPlan) {
  const used = new Set(plan.cells.map((c) => c.angle));
  return {
    concept: plan.concept || plan.cells[0]?.hook || "Concept",
    conceptDescription: plan.conceptDescription,
    angles: plan.angles.filter((a) => used.has(a.name)),
    cells: [...plan.cells].sort((a, b) => a.row - b.row).map(cellToWire),
  };
}

export function cellToWire(c: PlanCell) {
  return {
    hook: c.hook,
    hookBankId: c.hookBankId,
    angle: c.angle,
    emotionalTrigger: c.emotionalTrigger,
    awarenessStage: c.awarenessStage,
    format: c.format,
    layout: c.layout,
    headline: c.headline,
    primaryText: c.primaryText,
    overlayText: c.overlayText.map((t) => t.trim()).filter(Boolean),
    cta: c.cta,
    visualBrief: c.visualBrief,
    assetIds: c.assetIds,
    rationale: c.rationale,
  };
}
